import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resumeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { ArrowLeft, Mail, Phone, MapPin, Building, Edit, Trash2, Briefcase, Globe, Calendar, Clock, DollarSign, FileText, User } from 'lucide-react';

export default function ResumeDetailPage() {
  const { recno } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    resumeAPI.get(recno).then(({ data }) => {
      setR(data);
      setLoading(false);
      const name = `${(data.fname || '').trim()} ${(data.lname || '').trim()}`.trim();
      document.title = name ? `TalentVault - ${name}` : 'TalentVault';
    }).catch(() => { toast.error('Resume not found'); navigate('/search'); });
    return () => { document.title = 'TalentVault'; };
  }, [recno, navigate]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this resume permanently?')) return;
    try { await resumeAPI.delete(recno); toast.success('Deleted'); navigate('/search'); } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="page-container"><div className="loading-spinner"><div className="spinner" /></div></div>;
  if (!r) return null;

  const trim = (v) => (v || '').trim();
  const initials = `${(r.fname || '?')[0]}${(r.lname || '')[0] || ''}`.toUpperCase();

  const InfoRow = ({ label, value, icon: Icon }) => {
    const v = trim(value);
    if (!v) return null;
    return <div className="info-item">{Icon && <Icon size={16} />}<span><strong style={{ color: 'var(--text-muted)', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}: </strong>{v}</span></div>;
  };

  const DetailRow = ({ label, value }) => {
    const v = trim(value);
    if (!v) return null;
    return <div className="detail-row"><span className="label">{label}</span><span className="value">{v}</span></div>;
  };

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 28 }}>
        <button className="btn btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={18} /> Back</button>
        {isAdmin && (
          <div className="btn-group">
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/resumes/${recno}/edit`)}><Edit size={14} /> Edit</button>
            <button className="btn btn-danger btn-sm" onClick={handleDelete}><Trash2 size={14} /> Delete</button>
          </div>
        )}
      </div>

      {/* Header Card */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-body">
          <div className="resume-detail-header">
            <div className="resume-avatar-lg">{initials}</div>
            <div className="resume-meta">
              <h2>{r.fname} {r.mname ? r.mname + ' ' : ''}{r.lname}</h2>
              <p className="subtitle">{trim(r.employer) && `at ${trim(r.employer)}`} {trim(r.visastatus) && `· ${trim(r.visastatus)}`}</p>
              <div className="resume-info-grid">
                {trim(r.email) && <div className="info-item"><Mail size={16} />{trim(r.email)}</div>}
                {(trim(r.city) || trim(r.state) || trim(r.country)) && <div className="info-item"><MapPin size={16} />{[trim(r.city), trim(r.state), trim(r.country)].filter(Boolean).join(', ')}</div>}
                {trim(r.employer) && <div className="info-item"><Building size={16} />{trim(r.employer)}</div>}
                {trim(r.location) && <div className="info-item"><Globe size={16} />{trim(r.location)}</div>}
                {trim(r.rate) && <div className="info-item"><DollarSign size={16} />Rate: {trim(r.rate)}/{trim(r.unit1) || 'hr'}</div>}
                {trim(r.visastatus) && <div className="info-item"><FileText size={16} />Visa: {trim(r.visastatus)}</div>}
              </div>
            </div>
          </div>
          <p style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>Record # {r.recno}</p>
        </div>
      </div>

      <div className="detail-grid">
        {/* Left Column */}
        <div>
          {/* Comment / Summary */}
          {trim(r.comment) && (
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-body"><div className="detail-section"><h3>Comments / Summary</h3><div className="content">{trim(r.comment)}</div></div></div>
            </div>
          )}

          {/* Resume Text */}
          {trim(r.resume) && (
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-body"><div className="detail-section"><h3>Resume</h3><div className="content" style={{ maxHeight: 600, overflowY: 'auto' }}>{trim(r.resume)}</div></div></div>
            </div>
          )}

          {/* Reference */}
          {trim(r.reference) && (
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-body"><div className="detail-section"><h3>References</h3><div className="content">{trim(r.reference)}</div></div></div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div>
          {/* All Details */}
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-body">
              <div className="detail-section"><h3>Candidate Details</h3></div>
              <DetailRow label="CSG Comment" value={r.csgcomment} />
              <DetailRow label="Location" value={r.location} />
              <DetailRow label="Country Code" value={r.loccontry} />
              <DetailRow label="Visa Status" value={r.visastatus} />
              <DetailRow label="Visa Date" value={r.vdate} />
              <DetailRow label="Employer" value={r.employer} />
              <DetailRow label="Vendor" value={r.vendorname} />
              <DetailRow label="Rate" value={r.rate ? `${trim(r.rate)}/${trim(r.unit1) || 'hr'}` : null} />
              <DetailRow label="Condition" value={r.condition} />
              <DetailRow label="Request ID" value={r.requestid} />
              <DetailRow label="Int. Contact" value={r.intcontact} />
              <DetailRow label="Availability" value={r.availablty} />
              <DetailRow label="Date Available" value={r.dtavail} />
              <DetailRow label="US Experience" value={r.usexp} />
              <DetailRow label="Relocation" value={r.relocation} />
              <DetailRow label="Hold" value={r.hold1} />
              <DetailRow label="Send Mail Flag" value={r.sendmail} />
              <DetailRow label="Submitted" value={r.dtsubmit || (r.dtsbmt ? new Date(r.dtsbmt).toLocaleDateString() : null)} />
              <DetailRow label="ID String" value={r.idstring} />
              <DetailRow label="Zip" value={r.zip} />
              <DetailRow label="Remove Desc" value={r.removedesc} />
              <DetailRow label="New State" value={r.newstate} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
